export type FunnelStage =
    | 'identified'
    | 'qualified'
    | 'discovery'
    | 'sized'
    | 'proposal'
    | 'verbal'
    | 'closed'
    | 'launch_install';

export interface FunnelStageConfig {
    key: FunnelStage;
    label: string;
    description: string;
    color: string;
    order: number;
}

// FiyrPod brand palette — lime green progression from pale → rich
// Mirrors the Partnership Earnings page aesthetic
export const FIYRPOD_GREEN = '#76C442'; // brand lime
export const FIYRPOD_DARK = '#0d1b2e'; // deep navy (banner backgrounds)
export const FIYRPOD_CARD = '#111f33'; // card dark bg
export const FIYRPOD_BORDER = '#1e3050'; // dark border
export const FIYRPOD_ORANGE = '#e85d04'; // fire / emphasis
export const FIYRPOD_MUTED = '#8ba3be'; // muted text on dark

export const FUNNEL_STAGES: FunnelStageConfig[] = [
    {
        key: 'identified',
        label: 'Identified',
        description: 'Sponsor names the prospect',
        color: '#cde9a2', // pale lime — just entering the funnel
        order: 0,
    },
    {
        key: 'qualified',
        label: 'Qualified',
        description: 'Good fit by facility type, size, and athlete profile',
        color: '#b4e07a', // light lime
        order: 1,
    },
    {
        key: 'discovery',
        label: 'Discovery',
        description: 'Call/meeting confirms use case, budget, athlete counts, facility details',
        color: '#98d44c', // medium lime
        order: 2,
    },
    {
        key: 'sized',
        label: 'Sized',
        description: 'Estimate of indoor units, beacons, outdoor units, and install scope',
        color: '#7dc42a', // bright lime — heating up
        order: 3,
    },
    {
        key: 'proposal',
        label: 'Proposal',
        description: 'Proposal sent',
        color: '#76C442', // FiyrPod brand lime — prime territory
        order: 4,
    },
    {
        key: 'verbal',
        label: 'Verbal',
        description: 'Letter of Intent (LOI)',
        color: '#8dcf3a', // warm lime — almost there
        order: 5,
    },
    {
        key: 'closed',
        label: 'Closed',
        description: 'Contract signed',
        color: '#5bbe2a', // rich green — WON
        order: 6,
    },
    {
        key: 'launch_install',
        label: 'Launch/Install',
        description: 'Hardware and install execution',
        color: '#76C442', // FiyrPod brand — full circle, fully live
        order: 7,
    },
];

export const FUNNEL_STAGE_MAP: Record<FunnelStage, FunnelStageConfig> = FUNNEL_STAGES.reduce(
    (acc, stage) => {
        acc[stage.key] = stage;
        return acc;
    },
    {} as Record<FunnelStage, FunnelStageConfig>,
);

export interface DiscoveryDetails {
    id: number;
    prospect_uuid: string;
    use_case: string | null;
    budget_range: string | null;
    athlete_count_confirmed: number | null;
    facility_details: string | null;
    meeting_date: string | null;
    meeting_notes: string | null;
}

export interface SizingDetails {
    id: number;
    prospect_uuid: string;
    indoor_units: number;
    outdoor_units: number;
    beacons: number;
    install_scope: string | null;
    estimated_value: number | null;
}

export interface ProposalDetails {
    id: number;
    prospect_uuid: string;
    proposal_sent_at: string | null;
    proposal_amount: number | null;
    proposal_notes: string | null;
}

export interface CloseDetails {
    id: number;
    prospect_uuid: string;
    loi_signed_at: string | null;
    contract_signed_at: string | null;
    contract_value: number | null;
}

export interface InstallDetails {
    id: number;
    prospect_uuid: string;
    install_start_date: string | null;
    install_end_date: string | null;
    indoor_units_installed: number;
    outdoor_units_installed: number;
    beacons_installed: number;
    install_status: 'pending' | 'in_progress' | 'completed';
    install_notes: string | null;
}

export interface StageDetail {
    uuid: string;
    prospect_uuid: string;
    stage: FunnelStage;
    notes: string | null;
}

export interface ActivityLogEntry {
    uuid: string;
    user_uuid: string;
    action: string;
    from_stage: string | null;
    to_stage: string | null;
    description: string | null;
    created_at: string;
    user?: {
        uuid: string;
        name: string;
    };
}

export interface SalesFunnelProspect {
    uuid: string;
    organization_uuid: string;
    linked_organization_uuid: string | null;
    prospect_name: string;
    contact_name: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    facility_type: string | null;
    facility_size: string | null;
    athlete_profile: string | null;
    athlete_count: number | null;
    current_stage: FunnelStage;
    identified_at: string | null;
    qualified_at: string | null;
    discovery_at: string | null;
    sized_at: string | null;
    proposal_at: string | null;
    verbal_at: string | null;
    closed_at: string | null;
    launch_install_at: string | null;
    lost_at: string | null;
    lost_reason: string | null;
    created_by_user_uuid: string;
    created_at: string;
    updated_at: string;
    days_in_current_stage: number;
    pipeline_value: number | null;

    // Loaded relationships
    discovery_details?: DiscoveryDetails;
    sizing_details?: SizingDetails;
    proposal_details?: ProposalDetails;
    close_details?: CloseDetails;
    install_details?: InstallDetails;
    stage_details?: StageDetail[];
    activity_log?: ActivityLogEntry[];
}

export interface FunnelDashboardStats {
    stage_counts: Record<FunnelStage, number>;
    total_prospects: number;
    total_pipeline_value: number;
    conversion_rate: number;
    avg_days_in_funnel: number | null;
    prospects_this_month: number;
    prospects_last_month: number;
    avg_days_per_stage: Record<string, number | null>;
    recent_activity: ActivityLogEntry[];
}

/**
 * Lightweight sponsor record returned by the eligible-sponsors endpoint.
 * Includes ALL Gold orgs (even new ones with zero prospects) for the admin dialog dropdown.
 */
export interface EligibleSponsor {
    organization_uuid: string;
    organization_name: string;
    active_prospects: number;
    closed_count: number;
    pipeline_value: number;
    conversion_rate: number;
}

export interface SponsorStat {
    organization_uuid: string;
    organization_name: string;
    total_prospects: number;
    active_prospects: number;
    closed_count: number;
    lost_count: number;
    pipeline_value: number;
    conversion_rate: number;
    avg_days_to_close: number | null;
    stale_count: number;
    stage_counts: Partial<Record<FunnelStage, number>>;
}

export interface MonthlyTrend {
    month: string; // "YYYY-MM"
    prospects_added: number;
    closed_count: number;
}

export interface AdminFunnelDashboardStats {
    stage_counts: Record<FunnelStage, number>;
    per_org_counts: Record<string, Record<string, number>>;
    total_pipeline_value: number;
    total_prospects: number;
    overall_conversion_rate: number;
    avg_time_to_close: number | null;
    stale_prospects_count: number;
    top_performers: Array<{ organization_uuid: string; closed_count: number }>;
    sponsor_stats: SponsorStat[];
    monthly_trends: MonthlyTrend[];
}

export interface ProspectCreatePayload {
    prospect_name: string;
    contact_name?: string;
    contact_email?: string;
    contact_phone?: string;
    facility_type?: string;
    facility_size?: string;
    athlete_profile?: string;
    athlete_count?: number;
    estimated_value?: number;
}

export interface StageDetailsPayload {
    stage: FunnelStage;
    notes?: string;

    // Discovery
    use_case?: string;
    budget_range?: string;
    athlete_count_confirmed?: number;
    facility_details?: string;
    meeting_date?: string;
    meeting_notes?: string;

    // Sizing
    indoor_units?: number;
    outdoor_units?: number;
    beacons?: number;
    install_scope?: string;
    estimated_value?: number;

    // Proposal
    proposal_sent_at?: string;
    proposal_amount?: number;
    proposal_notes?: string;

    // Close
    loi_signed_at?: string;
    contract_signed_at?: string;
    contract_value?: number;

    // Install
    install_start_date?: string;
    install_end_date?: string;
    indoor_units_installed?: number;
    outdoor_units_installed?: number;
    beacons_installed?: number;
    install_status?: 'pending' | 'in_progress' | 'completed';
    install_notes?: string;
}
